import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { buildQueryString, exportFile, showError, showSuccess } from "../../utils/utilities";
import { useFormik } from "formik";
import { get, toLower } from "lodash";
import { useTranslation } from "react-i18next";
import {
  dbDate,
  dbMonthLg,
  fileFormatXextension,
  langDate,
  langMonth,
  paginationDefaults,
} from "../../utils/properties";
import { Col, Row, DatePicker, Table, Modal, Alert } from "antd";
import {
  DownloadOutlined,
  ClearOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  RedoOutlined,
} from "@ant-design/icons";
import Button from "../../components/functional_components/Button/PFButton";
import { setLoading } from "../../redux/ducks/Loading";
import AccountingBookExport from "../../services/api/AccountingBookExport";
import { setFilters } from "../../redux/ducks/Filters";
import dayjs from "dayjs";

const { confirm } = Modal;

const ReportLg = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const dateFormat = langMonth[toLower(currentLanguage)];
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const [state, setState] = useState({
    filterLoading: false,
    tableContent: [],
    isAvailable: null,
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
  });
  useEffect(() => {
    const queryString = buildQueryString(filters.obj, {});
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  }, []);
  const dataList = async (queryString) => {
    dispatch(setLoading(true));
    const { error: infoError, data: infoData } = await AccountingBookExport.getBookExportsInfo();
    const { data, error, currentPage, pageSize, totalItems } = await AccountingBookExport.getBookExports(queryString);
    if (!error && data && !infoError) {
      setState((prevState) => ({
        ...prevState,
        tableContent: data || [],
        isAvailable: infoData?.isAvailable,
        pagination: {
          currentPage: currentPage ? currentPage + 1 : paginationDefaults.page,
          size: pageSize || paginationDefaults.size,
          totalItems: totalItems || paginationDefaults.totalItems,
        },
      }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };
  const deleteCallback = (yearMonth) => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        const deleteResponse = await AccountingBookExport.deleteBookExport(yearMonth);
        if (!deleteResponse.error) {
          showSuccess();
          const queryString = buildQueryString(filters.obj, {});
          dispatch(setFilters(queryString));
          dataList(queryString.string);
        } else {
          showError(get(deleteResponse, "error.messageCode", "ER000"));
        }
      },
      onCancel() {},
    });
  };
  const columns = [
    {
      title: t("reportLg.table.yearMonthFrom"),
      dataIndex: "yearMonthFrom",
      key: "yearMonthFrom",
      render: (yearMonthFrom) => dayjs(yearMonthFrom.toString(), dbMonthLg).format(langMonth[toLower(currentLanguage)]),
    },
    {
      title: t("reportLg.table.yearMonthTo"),
      dataIndex: "yearMonthTo",
      key: "yearMonthTo",
      render: (yearMonthTo) => dayjs(yearMonthTo.toString(), dbMonthLg).format(langMonth[toLower(currentLanguage)]),
    },
    {
      title: t("reportLg.table.requestDate"),
      dataIndex: "requestDate",
      key: "requestDate",
      render: (requestDate) => dayjs(requestDate, dbDate).format(langDate[toLower(currentLanguage)]),
    },
    {
      title: t("reportLg.table.status"),
      dataIndex: "status",
      key: "status",
      render: (status) => t(`reportLg.status.${status}`),
    },
    {
      title: t("general.actions"),
      key: "action",
      width: 80,
      render: (data) => {
        return (
          <div className="space-around">
            {data.status === "PLANNED" ? (
              <Button currentIcon={<LoadingOutlined className="icon-medium" />} type="transparent" />
            ) : (
              <Button
                clickCallback={() => downloadCallback(data.yearMonthFrom)}
                currentIcon={<DownloadOutlined className="icon-medium" />}
                type="transparent"
              />
            )}
            <Button
              clickCallback={() => deleteCallback(data.yearMonthFrom)}
              currentIcon={<DeleteOutlined className="icon-medium" />}
              isDisabled={data.status === "PLANNED"}
              type="transparent"
            />
          </div>
        );
      },
    },
  ];
  const formikFilter = useFormik({
    initialValues: {
      yearMonthFrom: null,
      yearMonthTo: null,
    },
    validate: (values) => {
      let errors = {};
      if (!values.yearMonthFrom) {
        errors.yearMonthFrom = "Required!";
      }
      if (!values.yearMonthTo) {
        errors.yearMonthTo = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      dispatch(setLoading(true));
      setState((prevState) => ({ ...prevState, filterLoading: true }));
      const { error } = await AccountingBookExport.createBookExport(
        dayjs(values.yearMonthFrom, dbMonthLg).format(dbMonthLg),
        dayjs(values.yearMonthTo, dbMonthLg).format(dbMonthLg)
      );
      if (!error) {
        dataList(filters.string);
        showSuccess();
      } else {
        showError(get(error, "messageCode", "ER000"));
      }
      formikFilter.resetForm();
      setState((prevState) => ({ ...prevState, filterLoading: false }));
      dispatch(setLoading(false));
    },
  });
  const downloadCallback = async (yearMonth) => {
    dispatch(setLoading(true));
    const exportResponse = await AccountingBookExport.downloadBookExport(yearMonth);
    const downloadType = fileFormatXextension["xlsx"];
    const blob = new Blob([exportResponse], { type: downloadType });
    if (blob.size > 0) {
      exportFile(blob, `report-${yearMonth}.xlsx`);
    } else {
      showError("fileError");
    }
    dispatch(setLoading(false));
  };
  return (
    <div className="filter-container">
      {state.isAvailable === false && (
        <Alert
          message="Attenzione! E' possibile creare un nuovo file da esportare solamente al termine dell'elaborazione di tutti i file in coda. Si invita a cliccare sul bottone adiacente per aggiornare la pagina."
          type="warning"
          className="mb-1em"
          action={
            <Button
              clickCallback={() => dataList(filters.string)}
              currentIcon={<RedoOutlined className="icon-large" />}
            />
          }
        />
      )}
      <Row type="flex" justify="space-between">
        <Col lg={8} md={12} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mb-10" htmlFor="yearMonthFrom">
              {" "}
              {t("reportLg.filter.yearMonthFrom")}*
            </label>
          </Row>
          <Row>
            <Col span={20}>
              <DatePicker
                disabled={!state.isAvailable}
                className="w-100"
                picker="month"
                id="yearMonthFrom"
                name="yearMonthFrom"
                format={dateFormat}
                locale={langDate[`locale_${toLower(currentLanguage)}`]}
                value={formikFilter.values.yearMonthFrom}
                onChange={(date) => {
                  formikFilter.setFieldValue("yearMonthTo", null);
                  return formikFilter.setFieldValue("yearMonthFrom", date);
                }}
                disabledDate={(current) =>
                  formikFilter.values.yearMonthTo === null
                    ? false
                    : current && current > formikFilter.values.yearMonthTo
                }
              />
            </Col>
          </Row>
        </Col>
        <Col lg={8} md={12} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mb-10" htmlFor="yearMonthTo">
              {" "}
              {t("reportLg.filter.yearMonthTo")}*
            </label>
          </Row>
          <Row>
            <Col span={20}>
              <DatePicker
                disabled={!state.isAvailable}
                className="w-100"
                picker="month"
                id="yearMonthTo"
                name="yearMonthTo"
                format={dateFormat}
                locale={langDate[`locale_${toLower(currentLanguage)}`]}
                value={formikFilter.values.yearMonthTo}
                onChange={(date) => formikFilter.setFieldValue("yearMonthTo", date)}
                disabledDate={(current) =>
                  current &&
                  (current < formikFilter.values.yearMonthFrom ||
                    current > dayjs(formikFilter.values.yearMonthFrom).add(11, "months").endOf("month"))
                }
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row className="mt-1em mb-1em" justify="end">
        <Col>
          <Button
            isDisabled={!formikFilter.dirty || !state.isAvailable}
            type="secondary"
            currentSize="large"
            clickCallback={formikFilter.resetForm}
            currentIcon={<ClearOutlined className="icon-large" />}
          />
        </Col>
        <Col className="left-margin">
          <Button
            isDisabled={!(formikFilter.dirty && formikFilter.isValid) || !state.isAvailable}
            currentSize="large"
            isLoading={state.filterLoading}
            clickCallback={formikFilter.submitForm}
            currentIcon={<DownloadOutlined className="icon-large" />}
          />
        </Col>
      </Row>
      <Table
        rowKey={(item) => item.yearMonthFrom}
        dataSource={state.tableContent}
        columns={columns}
        size="small"
        pagination={{
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            const queryString = buildQueryString(filters.obj, { page: page - 1, size: pageSize });
            dispatch(setFilters(queryString));
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                pageSize: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            dataList(queryString.string);
          },
        }}
      />
    </div>
  );
};

export default ReportLg;
